'use client';

import { Fragment, useMemo } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
} from '@/components/ui/breadcrumb';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';
import Image from 'next/image';
import { ArrowCircleLeft } from '../../../public/icons';
import {
  crumbMap,
  dynamicCrumbMap,
  hiddenBreadcrumbPaths,
  isNonPageSegment,
} from '@/utilities/breadcrumb.ts/breadcrumbUtility';
import { useBreadcrumbStore } from '@/store/useBreadcrumbStore';

interface BreadcrumbData {
  label: string;
  path: string;
}

export function CustomBreadcrumb() {
  const pathname = usePathname();
  const router = useRouter();
  const dynamicLabels = useBreadcrumbStore((state) => state.dynamicLabels);

  const breadcrumbs = useMemo(() => {
    const segments = pathname.split('/').filter(Boolean);

    return segments
      .map((segment, index) => {
        const path = `/${segments.slice(0, index + 1).join('/')}`;
        const parentPath = '/' + segments.slice(0, index).join('/');

        if (isNonPageSegment(segment, parentPath)) return;

        const parentSegment = segments[index - 1];

        const label =
          dynamicLabels[path] ||
          crumbMap[path] ||
          dynamicCrumbMap[parentSegment] ||
          segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');

        return { label, path };
      })
      .filter(
        (crumb): crumb is BreadcrumbData =>
          crumb !== null &&
          crumb !== undefined &&
          !hiddenBreadcrumbPaths.includes(crumb.path)
      );
  }, [pathname, dynamicLabels]);

  if (breadcrumbs.length <= 1) return null;

  const firstCrumb = breadcrumbs[0];
  const lastCrumb = breadcrumbs[breadcrumbs.length - 1];
  const middleCrumbs = breadcrumbs.slice(1, -1);

  // In the case PRODUCT wants the back button to function this way
  // const handleBack = () => {
  //   // window.history.length tells us if there are pages to go back to.
  //   // Usually, a fresh tab has a length of 1 or 2.
  //   if (typeof window !== 'undefined' && window.history.length > 2) {
  //     router.back();
  //   } else {
  //     // If no history, navigate to the parent path logically.
  //     // e.g., /checkout/prefix-mapping/123 -> /checkout/prefix-mapping
  //     const segments = pathname.split('/').filter(Boolean);
  //     if (segments.length > 1) {
  //       const parentPath = `/${segments.slice(0, -1).join('/')}`;
  //       router.push(parentPath);
  //     } else {
  //       router.push('/dashboard');
  //     }
  //   }
  // };

  return (
    <Breadcrumb className='flex items-center gap-4'>
      <button
        onClick={() => router.back()}
        className='flex items-center gap-2 text-sm font-medium hover:opacity-70 transition-opacity cursor-pointer'
      >
        <Image src={ArrowCircleLeft} className='size-4' alt='back' />
        <span>Back</span>
      </button>

      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink
            className='text-[#1282A2] hover:text-[#1282A2] cursor-pointer'
            asChild
          >
            <Link href={firstCrumb?.path}>{firstCrumb?.label}</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>

        {middleCrumbs.length > 0 && (
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
        )}

        {middleCrumbs.length > 1 ? (
          <BreadcrumbItem>
            <DropdownMenu>
              <DropdownMenuTrigger className='flex items-center gap-1'>
                <BreadcrumbEllipsis />
                <ChevronDown className='size-3' />
              </DropdownMenuTrigger>
              <DropdownMenuContent align='start'>
                {middleCrumbs.map((crumb) => (
                  <DropdownMenuItem
                    className='text-[#1282A2] hover:text-[#1282A2] cursor-pointer'
                    key={crumb?.path}
                    asChild
                  >
                    <Link href={crumb.path}>{crumb.label}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </BreadcrumbItem>
        ) : (
          middleCrumbs.map((crumb) => (
            <Fragment key={crumb.path}>
              <BreadcrumbItem>
                <BreadcrumbLink
                  className='text-[#1282A2] hover:text-[#1282A2] cursor-pointer'
                  asChild
                >
                  <Link href={crumb.path}>{crumb.label}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
            </Fragment>
          ))
        )}

        <BreadcrumbSeparator>/</BreadcrumbSeparator>

        <BreadcrumbItem>
          <BreadcrumbPage className='font-semibold'>
            {lastCrumb.label}
          </BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
}

// 'use client';

// import {
//   Breadcrumb,
//   BreadcrumbEllipsis,
//   BreadcrumbItem,
//   BreadcrumbLink,
//   BreadcrumbList,
//   BreadcrumbPage,
//   BreadcrumbSeparator,
// } from '@/components/ui/breadcrumb';
// import {
//   DropdownMenu,
//   DropdownMenuContent,
//   DropdownMenuItem,
//   DropdownMenuTrigger,
// } from '@/components/ui/dropdown-menu';
// import { Fragment } from 'react';
// import { ChevronDown } from 'lucide-react';
// import {
//   crumbList,
//   hiddenBreadcrumbPaths,
// } from '@/utilities/breadcrumb.ts/breadcrumbUtility';
// import Link from 'next/link';
// import { usePathname, useRouter } from 'next/navigation';
// import { truncateString } from '@/utilities/string/truncateString';
// import Image from 'next/image';
// import { ArrowCircleLeft } from '../../../public/icons';

// export function CustomBreadcrumb() {
//   const pathname = usePathname();
//   const pathnames = pathname.split('/').filter((x) => x);
//   const router = useRouter();

//   // Hide breadcrumbs for specific routes
//   if (hiddenBreadcrumbPaths.includes(location.pathname)) return null;

//   // Function to match dynamic routes
//   const findMatchingRoute = (fullPath: string) => {
//     return (
//       crumbList.find((r) => r.path === fullPath) ||
//       crumbList.find((r) => {
//         const dynamicPathRegex = new RegExp(
//           `^${r.path.replace(/:\w+/g, '[^/]+')}$`
//         );
//         return dynamicPathRegex.test(fullPath);
//       })
//     );
//   };

//   // Generate breadcrumbs, filtering out unwanted routes
//   const breadcrumbs = pathnames
//     .map((path, index) => {
//       const fullPath = `/${pathnames.slice(0, index + 1).join('/')}`;
//       if (hiddenBreadcrumbPaths.includes(fullPath)) return null; // Skip hidden paths
//       const route = findMatchingRoute(fullPath);
//       return route && route.breadcrumb
//         ? { label: route.breadcrumb, path: fullPath }
//         : null;
//     })
//     .filter(Boolean); // Remove null values

//   // If no breadcrumbs, return null
//   if (breadcrumbs.length <= 1) return null;

//   return (
//     <Breadcrumb className='flex items-center gap-[1.125rem]'>
//       <button
//         className='flex gap-2.5 items-center text-sm cursor-pointer w-fit'
//         onClick={() => router.back()}
//       >
//         <Image src={ArrowCircleLeft} className='size-3.5' alt='nav back' /> Back
//       </button>
//       <BreadcrumbList className='flex items-center'>
//         {breadcrumbs.length > 3 ? (
//           <>
//             <BreadcrumbItem>
//               <BreadcrumbLink href={breadcrumbs[0]?.path} className=''>
//                 {breadcrumbs[0]?.label &&
//                   truncateString(breadcrumbs[0]?.label, 50)}
//               </BreadcrumbLink>
//             </BreadcrumbItem>
//             <BreadcrumbSeparator className='space-x-0'>/</BreadcrumbSeparator>
//             <BreadcrumbItem>
//               <DropdownMenu>
//                 <DropdownMenuTrigger className='flex items-center gap-1 focus:border-0 focus:outline-none'>
//                   <BreadcrumbEllipsis className='h-4 w-4' />{' '}
//                   <ChevronDown className='size-4' />
//                   <span className='sr-only'>Toggle menu</span>
//                 </DropdownMenuTrigger>
//                 <DropdownMenuContent align='start'>
//                   {breadcrumbs.slice(1, -1).map((crumb) =>
//                     crumb ? (
//                       <DropdownMenuItem key={crumb.path}>
//                         <Link
//                           href={crumb.path}
//                           className='text-[#101828] hover:underline'
//                         >
//                           {truncateString(crumb.label, 50)}
//                         </Link>
//                       </DropdownMenuItem>
//                     ) : null
//                   )}
//                 </DropdownMenuContent>
//               </DropdownMenu>
//             </BreadcrumbItem>
//             <BreadcrumbSeparator>/</BreadcrumbSeparator>
//             <BreadcrumbItem>
//               <BreadcrumbPage className=''>
//                 {breadcrumbs[breadcrumbs.length - 1]?.label}
//               </BreadcrumbPage>
//             </BreadcrumbItem>
//           </>
//         ) : (
//           breadcrumbs.map((crumb, index) =>
//             crumb ? (
//               <Fragment key={crumb.path}>
//                 {index > 0 && <BreadcrumbSeparator>/</BreadcrumbSeparator>}{' '}
//                 <BreadcrumbItem>
//                   {index === breadcrumbs.length - 1 ? (
//                     <BreadcrumbPage className='text-[#47536C] text-sm'>
//                       {crumb.label}
//                     </BreadcrumbPage>
//                   ) : (
//                     <BreadcrumbLink
//                       href={crumb.path}
//                       className='text-[#1282A2] text-sm'
//                     >
//                       {truncateString(crumb.label, 50)}
//                     </BreadcrumbLink>
//                   )}
//                 </BreadcrumbItem>
//               </Fragment>
//             ) : null
//           )
//         )}
//       </BreadcrumbList>
//     </Breadcrumb>
//   );
// }
